// @flow
import { uniq, sortBy, filter } from 'lodash';
import type { DownloadingContent } from 'Native/downloads/types';
import type { Franchise } from 'Common/franchise/types';

export const DownloadStatus = {
  NOT_DOWNLOADED: 'NOT_DOWNLOADED',
  DOWNLOADING: 'DOWNLOADING',
  DOWNLOADED: 'DOWNLOADED',
  DELETING: 'DELETING',
  SUSPENDED: 'SUSPENDED',
  FAILED: 'FAILED',
  QUEUED: 'QUEUED',
};

const isDownloaded = item => item.status === DownloadStatus.DOWNLOADED;

export const getDownloadedContent = (items: Array<DownloadingContent>) =>
  filter(items, item => isDownloaded(item));

export const getDownloadedContentByType = (type: string, items: Array<DownloadingContent>) =>
  filter(items, item => item.type === type && isDownloaded(item));

export const getQueuedDownloadingContent = (items: Array<DownloadingContent>) =>
  filter(items, item => !isDownloaded(item) && item.status !== DownloadStatus.NOT_DOWNLOADED);

export const getDownloadedContentBySlug = (slug: string, items: Array<DownloadingContent>) => {
  const franchise = items.find(item => item.slug === slug || item.franchise_slug === slug);
  return franchise && franchise.episode_id ? franchise.parent_franchise : franchise; // in case of episode we use its parent
};

export const mergeFranchiseBySlug = (downloads: Array<DownloadingContent>) => {
  const mergedDownloads = downloads.reduce((newFranchiseList, currentFranchise) => {
    const isExist = newFranchiseList.find(
      franchise =>
        franchise.franchise_slug && franchise.franchise_slug === currentFranchise.franchise_slug,
    );
    if (!isExist) {
      return [...newFranchiseList, currentFranchise];
    }
    return newFranchiseList;
  }, []);
  return mergedDownloads;
};

export const getDownloadingContentName = (item: DownloadingContent) =>
  item.name || item.franchise_name || '';

export const getFranchiseCount = (item: DownloadingContent, downloads: Array<DownloadingContent>) =>
  downloads.reduce((count, currentItem) => {
    if (
      item.franchise_slug &&
      currentItem.franchise_slug === item.franchise_slug &&
      isDownloaded(currentItem)
    ) {
      count += 1;
    }
    return count;
  }, 0);

export const getFranchiseAvailableSeasonsInOffline = (
  franchise: Franchise,
  downloads: Array<DownloadingContent>,
) => {
  const downloadedContent = getDownloadedContent(downloads).filter(
    item => item.franchise_slug && item.franchise_slug === franchise.slug,
  );
  return sortBy(uniq(downloadedContent.map(download => String(download.season_number))));
};
